import { SystemMetrics } from '../../types/operational';

interface SystemMetricsPanelProps {
  metrics: SystemMetrics;
}

export function SystemMetricsPanel({ metrics }: SystemMetricsPanelProps) {
  const getMetricColor = (value: number, thresholds = { warning: 70, critical: 90 }) => {
    if (value >= thresholds.critical) return 'text-red-600 bg-red-100';
    if (value >= thresholds.warning) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getProgressBarColor = (value: number, thresholds = { warning: 70, critical: 90 }) => {
    if (value >= thresholds.critical) return 'bg-red-500';
    if (value >= thresholds.warning) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const MetricItem = ({ 
    label, 
    value, 
    unit, 
    icon, 
    showProgress = true,
    thresholds = { warning: 70, critical: 90 }
  }: {
    label: string;
    value: number;
    unit: string;
    icon: string;
    showProgress?: boolean;
    thresholds?: { warning: number; critical: number };
  }) => (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className={`px-2 py-1 text-xs font-semibold rounded ${getMetricColor(value, thresholds)}`}>
          {value.toFixed(1)}{unit}
        </span>
      </div>
      {showProgress && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor(value, thresholds)}`}
            style={{ width: `${Math.min(value, 100)}%` }}
          ></div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Métriques Système</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricItem
          label="CPU"
          value={metrics.cpuUsage}
          unit="%"
          icon="🖥️"
        />
        <MetricItem
          label="Mémoire"
          value={metrics.memoryUsage}
          unit="%"
          icon="💾"
        />
        <MetricItem
          label="Disque"
          value={metrics.diskUsage}
          unit="%"
          icon="💿"
        />
        <MetricItem
          label="Latence Réseau"
          value={metrics.networkLatency}
          unit="ms"
          icon="🌐"
          showProgress={false}
          thresholds={{ warning: 50, critical: 100 }}
        />
        <MetricItem
          label="Connexions Actives"
          value={metrics.activeConnections}
          unit=""
          icon="🔗"
          showProgress={false}
          thresholds={{ warning: 500, critical: 800 }}
        />
        <MetricItem
          label="Requêtes/min"
          value={metrics.requestsPerMinute}
          unit=""
          icon="📊"
          showProgress={false}
          thresholds={{ warning: 3000, critical: 4500 }}
        />
      </div>
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <span className="text-blue-600">ℹ️</span>
          <span className="text-sm text-blue-800">
            Système opérationnel - Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
          </span>
        </div>
      </div>
    </div>
  );
}
